<!-- eslint-disable max-len -->
<template>
    <div class="guide-view" v-if="channelConfigs.length > 0">
        <div class="share-title">{{ shareTitle }}</div>
        <GuideTabContainer class="guide-view-tab">
            <GuideTab :channelConfigs="channelConfigs" v-if="isShowChannelTab"></GuideTab>
        </GuideTabContainer>

        <!-- 展示图片（包括快照拼接后的图片和普通图片） -->
        <GuideCard
            class="guide-view-card-image"
            :title="_lang('sharePopupImageLabel')"
            v-if="currentChannelImageList.length > 0"
        >
            <template #default>
                <SwiperContainer
                    class="guide-view-card-image-multiple"
                    :channel-image-list="currentChannelImageList"
                    :style="{ minHeight: expectedImageHeight || '5.04rem' }"
                    v-if="currentChannelImageList.length > 1"
                ></SwiperContainer>
                <img
                    class="guide-view-card-image-single"
                    :src="currentChannelImageList[0]"
                    :style="{ minHeight: expectedImageHeight || '5.12rem', opacity: isShowingPlaceholder ? 0 : 1 }"
                    alt=""
                    v-else
                />
            </template>
            <template #tool>
                <a
                    class="guide-card-toolbox-tool guide-card-toolbox-download"
                    @click="downloadImage(currentChannelImageList)"
                >
                    {{ _lang('sharePopupImageDownload') }}
                </a>
            </template>
        </GuideCard>
        <GuideCard :title="_lang('sharePopupTextLabel')" v-if="currentImageDescList.length > 0 && currentShowShareDesc">
            <template #default>
                <div class="guide-view-text-container">
                    <TextContainer
                        :text="currentImageDesc"
                        :channel="currentChannel"
                        :inviteLink="currentInviteLink"
                        @copy="
                            (text) => {
                                // 当 currentShowShareDesc 为 false（没有展示文案）时，不显示提示
                                codeText(text);
                            }
                        "
                    >
                    </TextContainer>
                    <Disclosure
                        v-if="currentDesc.disclosureDisplay"
                        :disclosure-outer="currentDesc.disclosureOuter"
                        :disclosure-popup="currentDesc.disclosurePopup"
                    />
                </div>
            </template>
            <template #tool>
                <a
                    v-if="currentImageDescList.length > 1"
                    class="guide-card-toolbox-tool guide-card-toolbox-change"
                    @click="changeImageDesc((activeImageDesc + 1) % currentImageDescList.length)"
                >
                    {{ _lang('sharePopupTextBtn') }}
                </a>
            </template>
        </GuideCard>
        <GuideCard :title="_lang('shareTip')" v-if="currentShowChannelTip">
            <template #default>
                <TextContainer
                    class="guide-view-text-container"
                    :text="currentChannelTip"
                    :channel="currentChannel"
                    :display-copy="false"
                ></TextContainer>
            </template>
        </GuideCard>
        <!-- 渠道图标展示（底部分享按钮模式） -->
        <div class="channel-icons-container" v-if="isShowChannelIcons">
            <a
                v-for="config in channelConfigs"
                :key="config.channel"
                class="channel-icon-item"
                :class="{ active: shouldShowActive(config.channel) }"
                @click="changeChannelByConfig(config.channel)"
            >
                <img
                    v-if="getChannelIcon(config.channel)"
                    :src="getChannelIcon(config.channel)"
                    :alt="config.channel"
                    class="channel-icon"
                />
                <div v-else class="channel-icon-placeholder">
                    {{ _lang(`names.${config.channel}`).charAt(0).toUpperCase() }}
                </div>
                <span class="channel-name">{{ _lang(`names.${config.channel}`) }}</span>
            </a>
        </div>
        <!-- 非底部分享按钮模式 -->
        <GuideFooter
            v-if="isShowFooterBtn"
            @share="doShare"
            :channel="currentChannel"
            :share-btn-text="currentChannelConfig.shareBtnText"
            :current-share-method="currentShareMethod"
        ></GuideFooter>
        <DisablePopup
            v-if="showDisablePopup"
            :channel="currentChannel"
            @close="showDisablePopup = false"
            @share="doSystemShare"
        ></DisablePopup>
    </div>
</template>

<!-- impact上报组件，指定多元归因参数下才会加载第三方sdk并上报 -->
<script >
import qrcode from 'qrcode';
import { computed, ref, watch } from 'vue-demi';
import { combineImages } from '../common/combine-image';
import { SHARE_TYPE, ShareEventMarket } from '../common/constants';
import { _lang } from '../common/lang';
import log from '../common/log';
import { appDataList, code, inviteLink } from '../common/payload';
import { shareReport } from '../common/report';
import { canShare, codeText, downloadImage, getShareMethod, share, showDisablePopup, systemShare } from '../common/share';
import { activeChannel, activeImage, activeImageDesc, changeImageDesc } from '../common/store';
import { Channel } from '../common/type';
import { getFegoData } from '../common/utils';
import DisablePopup from '../components/disable-popup.vue';
import Disclosure from '../components/disclosure.vue';
import GuideCard from '../components/guide-card.vue';
import GuideFooter from '../components/guide-footer.vue';
import GuideTabContainer from '../components/guide-tab-container.vue';
import GuideTab from '../components/guide-tab.vue';
import SwiperContainer from '../components/swiper-container.vue';
import TextContainer from '../components/text-container.vue';
import moomooLogo from '../images/moomoo.png';
import niuniuLogo from '../images/niuniu.png';
const __sfc_main = {};
__sfc_main.props = {
  channelConfigs: {
    type: Array,
    default: () => []
  },
  imageConfigs: {
    type: Array,
    default: () => []
  },
  descConfigs: {
    type: Array,
    default: () => []
  },
  market: {
    type: String,
    default: ''
  },
  shareTitle: {
    type: String,
    default: _lang('shareTitle')
  },
  channelDisplayLayout: {
    type: String,
    default: SHARE_TYPE.HORIZONTAL_TAB
  },
  inviteConfigs: {
    type: Array,
    default: () => []
  },
  reportAttrs: {
    type: Object,
    default: () => ({})
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const FEGODATA = computed(() => getFegoData());
  const props = __props;
  const emits = __ctx.emit;

  // 计算是否显示渠道切换tab
  const isShowChannelTab = computed(() => {
    return props.channelDisplayLayout === SHARE_TYPE.HORIZONTAL_TAB;
  });

  // 判断是否显示底部渠道图标
  const isShowChannelIcons = computed(() => {
    return props.channelDisplayLayout === SHARE_TYPE.BOTTOM_SHARE_BTN;
  });

  // 判断是否显示底部分享大按钮
  const isShowFooterBtn = computed(() => {
    return props.channelDisplayLayout === SHARE_TYPE.HORIZONTAL_TAB;
  });

  // 获取邀请链接（带参数）
  const currentInviteLink = computed(() => {
    const linkWithParams = generateInviteLinkWithParams();
    // 如果生成失败，回退到基础链接
    return linkWithParams || props.inviteConfigs[0]?.inviteLink || '';
  });
  /**
   * 获取渠道图标
   * @param {string} channel - 渠道类型
   * @returns {string} 图标路径
   */
  function getChannelIcon(channel) {
    // 使用 try-catch 来处理图标文件不存在的情况
    try {
      const iconMap = {
        [Channel.XIAOHONGSHU]: require('../images/channels/xiaohongshu.png'),
        [Channel.FACEBOOK]: require('../images/channels/facebook.png'),
        [Channel.TWITTER]: require('../images/channels/twitter.png'),
        [Channel.REDDIT]: require('../images/channels/reddit.png'),
        [Channel.TELEGRAM]: require('../images/channels/telegram.png'),
        [Channel.INSTAGRAM]: require('../images/channels/instagram.png'),
        [Channel.WECHAT]: require('../images/channels/wechat.png'),
        [Channel.WECHAT_MOMENTS]: require('../images/channels/wechat-moments.png')
      };
      return iconMap[channel] || '';
    } catch (error) {
      console.warn(`Channel icon not found for: ${channel}`, error);
      // 返回空字符串，组件会使用文字显示
      return '';
    }
  }

  // 标记用户是否已经点击过渠道
  const hasUserInteracted = ref(false);

  /**
   * 通过渠道配置切换渠道
   * @param {string} channel - 渠道类型
   */
  function changeChannelByConfig(channel) {
    hasUserInteracted.value = true;
    const index = props.channelConfigs.findIndex(config => config.channel === channel);
    if (index !== -1) {
      activeChannel.value = index;
      doShare();
    }
  }

  /**
   * 判断渠道是否应该显示 active 样式
   * 第一个渠道（index === 0）默认不显示 active 样式，但用户点击后可以显示
   * @param {string} channel - 渠道类型
   * @returns {boolean} 是否显示 active 样式
   */
  function shouldShowActive(channel) {
    const index = props.channelConfigs.findIndex(config => config.channel === channel);
    // 如果是第一个渠道且用户还没有交互过，不显示 active 样式
    if (index === 0 && activeChannel.value === 0 && !hasUserInteracted.value) {
      return false;
    }
    // 其他情况正常判断
    return currentChannel.value === channel;
  }

  /**
   * 生成带参数的邀请链接
   * 从 inviteConfigs[0] 获取基础链接和参数进行拼接
   * @returns {string} 完整的邀请链接（包含参数）
   */
  function generateInviteLinkWithParams() {
    // 从 props.inviteConfigs[0] 获取邀请配置
    const inviteConfig = props.inviteConfigs?.[0];
    if (!inviteConfig) {
      console.warn('inviteConfigs 为空，无法生成二维码');
      return '';
    }

    // 获取基础 URL：inviteLink
    const baseUrl = inviteConfig.inviteLink || 'http://promo.moomoo.com';

    // 构建参数对象
    const paramsObject = {
      promote_id: inviteConfig.promote_id,
      sub_promote_id: inviteConfig.sub_promote_id,
      invite: getFegoData().UID
    };
    if (!paramsObject.promote_id || !paramsObject.sub_promote_id) {
      console.warn('缺少必要的邀请参数:', {
        promote_id: paramsObject.promote_id,
        sub_promote_id: paramsObject.sub_promote_id,
        inviteConfig
      });
      return '';
    }

    // JSON 编码并 URL 编码
    const encodedContent = encodeURIComponent(JSON.stringify(paramsObject));

    // 生成完整 URL
    const fullUrl = `${baseUrl}?${encodedContent}`;
    return fullUrl;
  }

  // 描述映射
  const descMap = computed(() => {
    const map = {};
    props.descConfigs.forEach(item => {
      map[item.id] = item;
    });
    return map;
  });

  // 图片映射
  const imageMap = computed(() => {
    const map = {};
    props.imageConfigs.forEach(item => {
      map[item.id] = item;
    });
    return map;
  });

  // 判断某个图片配置是否需要快照
  const needSnapshot = imageConfig => {
    return !!(imageConfig.inviteQrcodeDisplay || imageConfig.inviteTitleDisplay || imageConfig.inviteCodeDisplay);
  };

  // 创建一个透明的1x1像素占位符图片（base64）
  const placeholderImage = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

  /**
   * 获取二维码占位图片
   * 用于在没有配置二维码但有其他拼接内容时占位
   * 根据 market 参数选择不同的 logo 图片
   * @param {string} market - 市场标识，'1'（HK）使用 niuniu.png，否则使用 moomoo.png
   * @returns {string} 图片路径
   */
  function getQrcodePlaceholderImage(market) {
    // 如果 market 是 '1'（HK），使用 niuniu.png，否则使用 moomoo.png
    return market === ShareEventMarket.HK ? niuniuLogo : moomooLogo;
  }

  // 判断当前图片是否需要拼接
  const currentImageNeedSnapshot = computed(() => {
    return needSnapshot(currentImageConfig.value);
  });

  // 检查当前渠道是否有任何图片需要拼接（用于提前预留空间）
  const hasAnyImageNeedSnapshot = computed(() => {
    return currentChannelImageConfigList.value.some(config => needSnapshot(config));
  });

  // 计算拼接图的预期高度（用于预留空间，避免尺寸变化）
  // 拼接图高度 = 原图高度 * 1.23（原图高度 + 23% 的底部 padding）
  const expectedImageHeight = computed(() => {
    // 如果当前渠道有任何图片需要拼接，就提前预留空间
    if (!hasAnyImageNeedSnapshot.value) {
      return null; // 不需要拼接，使用默认高度
    }
    // 单张图片：使用固定高度 5.12rem（对应原图高度），拼接后高度约为 5.12 * 1.23 ≈ 6.3rem
    // 多张图片（SwiperContainer）：使用固定高度 4.56rem（对应原图高度），拼接后高度约为 4.56 * 1.23 ≈ 5.6rem
    const isMultiple = currentChannelImageConfigList.value.length > 1;
    return isMultiple ? '5.6rem' : '6.3rem';
  });

  // 当前渠道配置
  const currentChannelConfig = computed(() => props.channelConfigs[activeChannel.value]);
  // 当前渠道
  const currentChannel = computed(() => currentChannelConfig.value?.channel);
  // 当前渠道是否展示分享文案
  const currentShowShareDesc = computed(() => currentChannelConfig.value?.showShareDesc);
  // 当前渠道是否展示小贴士
  const currentShowChannelTip = computed(() => currentChannelConfig.value?.showChannelTip);
  // 当前渠道小贴士
  const currentChannelTip = computed(() => descMap.value[currentChannelConfig.value?.channelTip].text);
  // 当前渠道图片配置列表
  // eslint-disable-next-line max-len
  const currentChannelImageConfigList = computed(() => (currentChannelConfig.value?.image_ids || []).map(id => imageMap.value[id]).filter(config => config) // 过滤掉 undefined 的配置
  );

  // 存储处理后的图片列表（包括快照图片和普通图片）
  const processedImageList = ref([]);
  const isProcessingImages = ref(false);
  // 保存上一个渠道的图片列表，用于切换渠道时先显示，避免闪烁
  const previousChannelImageList = ref([]);

  // 图片处理缓存：key 为缓存键，value 为处理后的图片 URL
  // 缓存键包含渠道、图片ID、邀请码等关键参数，确保不同渠道和不同邀请码的图片分别缓存
  // 例如：wechat_image1_CODE_A 和 facebook_image1_CODE_B 会分别缓存，互不影响
  const imageCache = new Map();

  /**
   * 生成图片缓存键
   * 注意：缓存键包含渠道和邀请码，因此不同渠道即使使用相同图片配置，也会生成不同的缓存键
   * 这样可以确保：
   * 1. 不同渠道的邀请码不同时，会分别缓存各自的拼接图
   * 2. 切换回之前的渠道时，如果邀请码没变，可以直接使用缓存，避免重复绘制
   * @param {string} channel - 渠道（如 wechat, facebook）
   * @param {string} imageId - 图片ID
   * @param {string} inviteCode - 邀请码（不同渠道可能不同）
   * @param {string} qrcodeUrl - 二维码URL
   * @param {string} inviteTitle - 邀请标题
   * @returns {string} 缓存键，格式：渠道_图片ID_邀请码_二维码URL_标题
   */
  function generateCacheKey(channel, imageId, inviteCode, qrcodeUrl, inviteTitle) {
    return `${channel}_${imageId}_${inviteCode}_${qrcodeUrl || ''}_${inviteTitle || ''}`;
  }

  // 处理图片列表：对于需要快照的图片生成拼接图，否则使用原始url
  async function processImageList() {
    const imageConfigList = currentChannelImageConfigList.value;
    if (!imageConfigList || imageConfigList.length === 0) {
      processedImageList.value = [];
      return;
    }

    // 根据当前渠道获取对应的邀请码（与 text-container 使用相同的逻辑）
    const channel = currentChannel.value;
    let inviteCode = code.value;

    // 如果有渠道和 appDataList，获取渠道对应的邀请码
    if (channel && appDataList.value?.length) {
      const appData = appDataList.value.find(item => item.channel === channel);
      inviteCode = appData?.code || code.value;
    }

    // 过滤占位符 '{code}'，确保获取真实邀请码
    if (!inviteCode || inviteCode === '{code}') {
      inviteCode = 'TEST_CODE';
    }

    // 预生成邀请链接（如果多个图片都需要二维码，可以复用）
    const qrcodeUrl = generateInviteLinkWithParams();
    isProcessingImages.value = true;
    try {
      const results = await Promise.all(imageConfigList.map(async imageConfig => {
        // 防御性检查：如果 imageConfig 不存在，返回 null
        if (!imageConfig) {
          return null;
        }

        // 判断是否需要快照拼接
        if (needSnapshot(imageConfig)) {
          // 获取邀请码（只有当 inviteCodeDisplay 为 true 时才使用）
          const inviteCodeValue = imageConfig.inviteCodeDisplay ? inviteCode : '';

          // 获取标题
          const inviteTitle = imageConfig.inviteTitleDisplay ? imageConfig.inviteTitle || '' : '';

          // 生成缓存键
          const cacheKey = generateCacheKey(channel, imageConfig.id, inviteCodeValue, qrcodeUrl, inviteTitle);

          // 检查缓存
          if (imageCache.has(cacheKey)) {
            return imageCache.get(cacheKey);
          }

          // 生成二维码图片（base64）
          let inviteQrcode = '';
          if (imageConfig.inviteQrcodeDisplay) {
            try {
              if (qrcodeUrl) {
                // 使用 qrcode 库生成 base64 图片（高分辨率）
                inviteQrcode = await qrcode.toDataURL(qrcodeUrl, {
                  width: 400,
                  // 生成 400px，确保清晰度
                  margin: 2,
                  errorCorrectionLevel: 'H' // 使用最高纠错级别
                });
              }
            } catch (qrError) {
              log.error('二维码生成失败:', qrError);
            }
          }

          // 如果没有配置二维码但有其他拼接内容（邀请码或标题），使用占位图片
          if (!inviteQrcode && (inviteCodeValue || inviteTitle)) {
            inviteQrcode = getQrcodePlaceholderImage(props.market);
          }

          // 如果配置了快照但没有必要的信息，直接返回原图
          if (!inviteCodeValue && !inviteQrcode && !inviteTitle) {
            const originalUrl = imageConfig.url;
            imageCache.set(cacheKey, originalUrl);
            return originalUrl;
          }

          // 使用 combineImages 生成拼接图
          // 不传 width 和 height，保持原图尺寸不变
          const combinedImage = await combineImages([imageConfig.url, inviteCodeValue, inviteQrcode, inviteTitle, _lang('codeText')], {
            // 使用相对比例，确保不同尺寸图片拼接部分大小一致
            useRelativeSize: true
          });
          const resultImage = combinedImage || imageConfig.url;
          // 存入缓存
          imageCache.set(cacheKey, resultImage);
          return resultImage;
        } else {
          // 不需要快照，直接使用原始url（也可以缓存，但意义不大）
          return imageConfig.url;
        }
      }));
      processedImageList.value = results.filter(url => url);
      // 处理完成后，保存到上一个渠道的图片列表，用于切换渠道时先显示
      if (processedImageList.value.length > 0) {
        previousChannelImageList.value = [...processedImageList.value];
      }
    } catch (error) {
      console.error('处理图片列表失败:', error);
      // 失败时使用原始url列表
      processedImageList.value = imageConfigList.map(item => item?.url).filter(url => url);
    } finally {
      isProcessingImages.value = false;
    }
  }

  // 监听图片配置变化和邀请码变化，重新处理图片
  watch(() => {
    // 生成当前图片配置的唯一标识
    const imageIds = currentChannelImageConfigList.value.map(config => config?.id).join(',');
    const channel = currentChannel.value;
    let inviteCode = code.value;

    // 如果有渠道和 appDataList，获取渠道对应的邀请码
    if (channel && appDataList.value?.length) {
      const appData = appDataList.value.find(item => item.channel === channel);
      inviteCode = appData?.code || code.value;
    }
    return {
      imageIds,
      channel,
      inviteCode,
      fegoCode: FEGODATA.value.code,
      fegoInviteGuideCode: FEGODATA.value.inviteGuideCode
    };
  }, (newVal, oldVal) => {
    // 如果图片配置ID列表没有变化，且邀请码也没有变化，则不需要重新处理
    // 这样可以避免切换渠道时，如果图片配置相同就重复处理
    const imageIdsChanged = newVal.imageIds !== (oldVal?.imageIds || '');
    const inviteCodeChanged = newVal.inviteCode !== (oldVal?.inviteCode || '');
    const fegoCodeChanged = newVal.fegoCode !== (oldVal?.fegoCode || '');
    const fegoInviteGuideCodeChanged = newVal.fegoInviteGuideCode !== (oldVal?.fegoInviteGuideCode || '');

    // 只有当图片配置变化或邀请码相关参数变化时才重新处理
    if (imageIdsChanged || inviteCodeChanged || fegoCodeChanged || fegoInviteGuideCodeChanged || !oldVal) {
      processImageList();
    }
  }, {
    immediate: true
  });

  // 当前渠道图片列表（动态处理：快照图片 + 普通图片）
  const currentChannelImageList = computed(() => {
    // 如果正在处理图片
    if (isProcessingImages.value) {
      // 如果当前渠道有图片需要拼接，先显示上一个渠道的拼接图，避免显示未拼接的图
      if (hasAnyImageNeedSnapshot.value) {
        // 优先显示上一个渠道的拼接图（如果有）
        if (previousChannelImageList.value.length > 0) {
          return previousChannelImageList.value;
        }
        // 如果有当前渠道已处理的图片，也显示（可能是部分处理完成）
        if (processedImageList.value.length > 0) {
          return processedImageList.value;
        }
        // 否则返回占位符数组，保持容器显示和尺寸
        const imageCount = currentChannelImageConfigList.value.length;
        return imageCount > 0 ? new Array(imageCount).fill(placeholderImage) : [];
      }
      // 如果不需要拼接，可以显示原始图片
      return currentChannelImageConfigList.value.map(item => item?.url).filter(url => url);
    }
    // 处理完成，返回处理后的图片列表
    if (processedImageList.value.length === 0) {
      // 如果没有处理后的图片，返回原始url列表
      return currentChannelImageConfigList.value.map(item => item?.url).filter(url => url);
    }
    return processedImageList.value;
  });

  // 判断当前是否显示占位符
  const isShowingPlaceholder = computed(() => {
    return isProcessingImages.value && hasAnyImageNeedSnapshot.value && previousChannelImageList.value.length === 0 && processedImageList.value.length === 0 && currentChannelImageList.value.length > 0 && currentChannelImageList.value[0] === placeholderImage;
  });

  // 当前图片配置
  const currentImageConfig = computed(() => currentChannelImageConfigList.value?.[activeImage.value]);
  // 当前图片Id
  const currentImageId = computed(() => currentImageConfig.value?.id);
  // 当前图片（使用处理后的图片，包括快照）
  const currentImage = computed(() => {
    // 使用处理后的图片列表（包含快照拼接后的图片）
    return currentChannelImageList.value?.[activeImage.value] || currentImageConfig.value?.url;
  });
  // 当前图片描述列表
  const currentImageDescList = computed(() => currentImageConfig.value?.desc_ids || []);
  // 当前图片描述id
  const currentImageDescId = computed(() => currentImageDescList.value?.[activeImageDesc.value]);
  // 当前图片描述
  // eslint-disable-next-line max-len
  const currentImageDesc = computed(() => descMap.value[currentImageDescId.value].text);
  // 当前描述
  const currentDesc = computed(() => descMap.value[currentImageDescId.value]);
  // 当前分享方式
  const currentShareMethod = computed(() => getShareMethod({
    channel: currentChannel.value
  }));

  // 分享
  function getReportAttrsFromEvent(evt) {
    // 从点击元素开始向上找最近的 data-id/data-name
    try {
      const el = evt?.target;
      const node = el?.closest ? el.closest('[data-id], [data-name]') : null;
      console.log('node', node);
      return {
        element_id: node?.getAttribute?.('data-id') || '',
        element_name: node?.getAttribute?.('data-name') || ''
      };
    } catch (e) {
      return {
        element_id: '',
        element_name: ''
      };
    }
  }
  function doShare(evt) {
    const channel = currentChannel.value;
    const image = currentImage.value;
    const imageId = currentImageId.value;
    const desc = currentImageDesc.value;
    const descId = currentImageDescId.value;
    if (canShare(channel)) {
      shareReport({
        channel,
        material_id: imageId,
        string_id: descId,
        user_home_market: props.market,
        report_attrs: props.reportAttrs
      });
      share({
        channel,
        image,
        imageId,
        desc,
        descId,
        inviteLink: currentInviteLink.value || inviteLink.value,
        code: code.value,
        appDataList: appDataList.value,
        showShareDesc: currentShowShareDesc.value
      });
      emits('shareSuccess');
    } else {
      showDisablePopup.value = true;
    }
  }
  function doSystemShare() {
    shareReport({
      channel: Channel.SYSTEM,
      material_id: '',
      string_id: '',
      user_home_market: props.market,
      report_attrs: props.reportAttrs
    });
    systemShare(inviteLink.value);
  }

  // 暴露方法给父组件调用
  return Object.assign({
    _lang,
    codeText,
    downloadImage,
    showDisablePopup,
    activeImageDesc,
    changeImageDesc,
    isShowChannelTab,
    isShowChannelIcons,
    isShowFooterBtn,
    currentInviteLink,
    getChannelIcon,
    changeChannelByConfig,
    shouldShowActive,
    expectedImageHeight,
    currentChannelConfig,
    currentChannel,
    currentShowShareDesc,
    currentShowChannelTip,
    currentChannelTip,
    currentChannelImageList,
    isShowingPlaceholder,
    currentImageDescList,
    currentImageDesc,
    currentDesc,
    currentShareMethod,
    doShare,
    doSystemShare
  }, {
    doShare
  });
};
__sfc_main.components = Object.assign({
  GuideTabContainer,
  GuideTab,
  GuideCard,
  SwiperContainer,
  TextContainer,
  Disclosure,
  GuideFooter,
  DisablePopup
}, __sfc_main.components);
export default __sfc_main;
</script>

<style lang="less" scoped>
.guide-view {
    .share-title {
        color: #262626;
        font-size: 0.48rem;
        font-style: normal;
        font-weight: 600;
        line-height: 130%;
        margin: 0.4rem 0.3rem;
    }

    .guide-view-tab {
        margin-bottom: 0.2rem;
    }

    .channel-icons-container {
        display: flex;
        flex-wrap: nowrap;
        overflow-x: auto;
        overflow-y: hidden;
        gap: 0.2rem;
        padding: 0.3rem;
        margin-bottom: 0.2rem;
        -webkit-overflow-scrolling: touch; /* iOS 平滑滚动 */
        scrollbar-width: none; /* Firefox 隐藏滚动条 */
        -ms-overflow-style: none; /* IE 和 Edge 隐藏滚动条 */

        /* Chrome、Safari 隐藏滚动条 */
        &::-webkit-scrollbar {
            display: none;
        }

        .channel-icon-item {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 0.2rem;
            cursor: pointer;
            transition: all 0.3s ease;
            flex-shrink: 0;
            width: calc((100% - 0.6rem - 0.8rem) / 5); /* 一行展示5个：(总宽度 - 左右padding - 4个gap) / 5 */
            min-width: 1.2rem; /* 最小宽度保证内容不会太挤 */
            position: relative;

            &.active {
                .channel-icon {
                    border-color: #ff4d4f; /* 红色圆形边框 */
                }

                .channel-icon-placeholder {
                    border-color: #ff4d4f; /* 红色圆形边框 */
                }

                .channel-name {
                    color: #ff6900; /* 橙红色文字 */
                    font-weight: 600;
                }
            }

            .channel-icon {
                width: 0.64rem;
                height: 0.64rem;
                object-fit: contain;
                margin-bottom: 0.08rem;
                transition: all 0.3s ease;
                box-sizing: content-box;
                /* 为非 active 状态也添加透明边框和 padding，保持尺寸一致，避免抖动 */
                border: 0.04rem solid transparent;
                border-radius: 50%;
                padding: 0.03rem;
            }

            .channel-icon-placeholder {
                width: 0.64rem;
                height: 0.64rem;
                border-radius: 50%;
                background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                display: flex;
                align-items: center;
                justify-content: center;
                color: #ffffff;
                font-size: 0.32rem;
                font-weight: 600;
                margin-bottom: 0.08rem;
                transition: all 0.3s ease;
                /* 为非 active 状态也添加透明边框，保持尺寸一致，避免抖动 */
                border: 0.04rem solid transparent;
            }

            .channel-name {
                font-size: 0.24rem;
                color: #666666;
                text-align: center;
                line-height: 1.2;
                transition: all 0.3s ease;
                /* 固定最小高度，避免 font-weight 变化导致的高度抖动 */
                min-height: 0.288rem; /* 0.24rem * 1.2 = 0.288rem */
                display: flex;
                align-items: center;
                justify-content: center;
            }
        }
    }

    .guide-view-card-image {
        height: auto;

        &-multiple {
            margin-top: 0.3rem;
        }

        &-single {
            width: 3.84rem;
            height: 5.12rem;
            flex-shrink: 0;
            object-fit: contain;
            margin-top: 0.4rem;
            margin-left: 1.83rem;
            margin-right: 1.83rem;
        }
    }
    .guide-view-text-container {
        margin-top: 0.2rem;
    }

    .guide-card-toolbox-tool {
        font-size: 0.24rem;
        color: #1378f4;
        font-style: normal;
        font-weight: 400;
        line-height: normal;
        padding-left: 0.22rem;
        white-space: nowrap;
    }

    .guide-card-toolbox-download {
        background: url('../images/download.svg') no-repeat left center;
        background-size: 0.2rem 0.2rem;
    }

    .guide-card-toolbox-change {
        background: url('../images/change.svg') no-repeat left center;
        background-size: 0.2rem 0.2rem;
    }
}
</style>
